// Register via invite-token. PRD-01 US-AUTH-04.
// /register?token=<invite-token>

import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import { authStore, type CurrentUser } from "@/lib/auth";

interface InviteInfo {
  email: string;
  role: string;
  tenantName: string;
  tenantSlug: string;
  expiresAt: string;
}

interface RegisterResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: CurrentUser;
  tenantSlug: string;
}

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setInviteError("auth.register.noToken");
      return;
    }
    api
      .get<InviteInfo>(`/v1/invites/validate/${encodeURIComponent(token)}`, { skipAuth: true })
      .then(setInvite)
      .catch((e) =>
        setInviteError(e instanceof ApiError ? e.i18nKey : "errors.internal")
      );
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    if (password !== passwordConfirm) {
      setErrorKey("auth.register.passwordMismatch");
      return;
    }
    setErrorKey(null);
    setLoading(true);
    try {
      const data = await api.post<RegisterResponse>(
        "/v1/auth/register",
        { token, password, firstName, lastName },
        { skipAuth: true }
      );
      authStore.setSession(data.accessToken, data.refreshToken, data.user, data.tenantSlug);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setErrorKey(err instanceof ApiError ? err.i18nKey : "errors.internal");
    } finally {
      setLoading(false);
    }
  }

  if (inviteError) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md bg-white border border-red-200 rounded-2xl p-6 text-center">
          <h1 className="text-xl font-semibold mb-2 text-red-800">
            {t("auth.register.invalidInvite", "Uitnodiging ongeldig")}
          </h1>
          <p className="text-sm text-neutral-700 mb-4">
            {t(inviteError, "De link is verlopen of niet geldig. Vraag een nieuwe uitnodiging aan.")}
          </p>
          <button
            onClick={() => navigate("/login", { replace: true })}
            className="px-4 py-2 bg-brand-500 text-white rounded-lg"
          >
            {t("common.back", "Terug naar inloggen")}
          </button>
        </div>
      </main>
    );
  }

  if (!invite) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <p className="text-neutral-500">{t("common.loading", "Laden…")}</p>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-brand-50 to-white">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-neutral-200 p-8">
        <h1 className="text-2xl font-semibold mb-1">
          {t("auth.register.title", "Account activeren")}
        </h1>
        <p className="text-sm text-neutral-600 mb-2">
          {t("auth.register.subtitle", "Welkom bij {{tenant}}", { tenant: invite.tenantName })}
        </p>
        <div className="mb-6 inline-flex items-center px-2 py-1 rounded bg-brand-50 text-brand-700 text-xs font-medium">
          {t("auth.register.role", "Rol")}: {invite.role}
        </div>

        <form onSubmit={onSubmit} className="space-y-4" aria-busy={loading}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-neutral-700 mb-1">
                {t("auth.register.firstName", "Voornaam")}
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoComplete="given-name"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-neutral-700 mb-1">
                {t("auth.register.lastName", "Achternaam")}
              </label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                autoComplete="family-name"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              {t("auth.register.email", "E-mail")}
            </label>
            <input
              type="email"
              value={invite.email}
              readOnly
              className="w-full px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-lg text-neutral-600"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-1">
              {t("auth.register.password", "Wachtwoord")}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={12}
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            />
            <p className="mt-1 text-xs text-neutral-500">
              {t("auth.register.passwordHint", "Minimaal 12 tekens, met hoofdletter, cijfer en speciaal teken")}
            </p>
          </div>

          <div>
            <label htmlFor="pwconfirm" className="block text-sm font-medium text-neutral-700 mb-1">
              {t("auth.register.passwordConfirm", "Wachtwoord herhalen")}
            </label>
            <input
              id="pwconfirm"
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              required
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            />
          </div>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              required
              className="mt-0.5"
            />
            <span className="text-neutral-700">
              {t(
                "auth.register.terms",
                "Ik ga akkoord met de privacyverklaring en algemene voorwaarden."
              )}
            </span>
          </label>

          {errorKey && (
            <div role="alert" className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
              {t(errorKey)}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !acceptTerms}
            className="w-full px-4 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-medium rounded-lg"
          >
            {loading
              ? t("auth.register.activating", "Activeren…")
              : t("auth.register.submit", "Account activeren")}
          </button>
        </form>
      </div>
    </main>
  );
}
